import { createFetchRetry } from '@rsos/capstone';
import {
  normalizeAllSpatialLayers,
  partitionLayers,
  findCrgDefaultFloor,
} from '@rsos/components/capstone/common/Map/utils/spatialLayersHelpers';
import { mapLayerSettingsSelector } from '@rsos/reduxDataStore';
import { getTokens } from '@rsos/sinatra';
import { checkStatus, parseJSON } from '@rsos/utils';
import { getAPIHost } from '@rsos/utils/metaTags';
import serializeRequestError from '@rsos/utils/serializeRequestError';
import { normalizePhone } from '../components/JurisdictionView/AviveAED/aviveSlice';
import {
  AVIVE2_STATUS_AED_CANCELED,
  AVIVE2_STATUS_AED_CONCLUDED,
  AVIVE2_STATUS_EMERGENCY_TIMEOUT,
} from '../components/JurisdictionView/AviveAED/aviveStatusTypes';
import { getEnrichedDynamicLayers } from '../utils/getEnrichedDynamicLayers';
import {
  ADD_DYNAMIC_LAYER,
  CLEAR_CHECKED_LAYERS,
  DEACTIVATE_SPATIAL_LAYER,
  FETCH_CRG_DEEPLINK_START,
  FETCH_CRG_DEEPLINK_SUCCESS,
  FETCH_CRG_DEEPLINK_FAILED,
  FORCE_STATIC_LAYER_ON,
  GET_SPATIAL_LAYERS_START,
  GET_SPATIAL_LAYERS_SUCCESS,
  GET_SPATIAL_LAYERS_FAILED,
  GET_LATEST_ACTIVE_SPATIAL_LAYERS,
  GET_SPATIAL_OBJECTS_SUMMARY_SUCCESS,
  GET_SPATIAL_OBJECTS_SUMMARY_FAILURE,
  GET_SPATIAL_OBJECT_DETAILS_SUCCESS,
  SET_ACTIVE_CRG_OBJECT,
  INACTIVATE_AVIVE_REQUEST,
  SET_ACTIVE_CRG_OBJECT_FLOOR,
  SET_STATIC_SPATIAL_OBJECT_ACTIVE_STATUS,
  SET_STATIC_SPATIAL_OBJECT_INACTIVE_STATUS,
  TOGGLE_SPATIAL_LAYER,
  TOGGLE_SPATIAL_LAYER_GROUP,
  UPDATE_DYNAMIC_LAYER,
  UPDATE_DISABLED_DYNAMIC_LAYERS,
  FETCH_SPATIAL_OBJECT_DETAILS_START,
  FETCH_SPATIAL_OBJECT_DETAILS_FAILED,
} from './actionTypes';

const baseURL = `${getAPIHost()}`;

/**
 * Helper function to process and shape spatial layers data.
 * Applies map layer settings and partitions layers into static/dynamic.
 */
const processLayersResponse = (layers, getState) => {
  const settings = mapLayerSettingsSelector(getState());

  if (layers) {
    layers.forEach(layer => {
      if (settings[layer.id]) {
        layer.isChecked = settings[layer.id].default_checked;
      }
    });
  }

  const allSpatialLayers = normalizeAllSpatialLayers(layers);
  const [staticLayers, dynamicLayers] = partitionLayers(allSpatialLayers);
  const enrichedDynamicLayers = getEnrichedDynamicLayers(
    getState().spatialLayers.dynamicLayers,
    dynamicLayers,
  );

  return { staticLayers, dynamicLayers: enrichedDynamicLayers };
};

export const addDynamicLayer = layer => ({
  type: ADD_DYNAMIC_LAYER,
  layer,
});

export const fetchSpatialLayersStart = () => ({
  type: GET_SPATIAL_LAYERS_START,
});

export const fetchSpatialLayersSuccess = ({ staticLayers, dynamicLayers }) => ({
  type: GET_SPATIAL_LAYERS_SUCCESS,
  staticLayers,
  dynamicLayers,
});

export const fetchSpatialLayersFailed = error => ({
  type: GET_SPATIAL_LAYERS_FAILED,
  error,
});

export const clearCheckedLayers = () => ({
  type: CLEAR_CHECKED_LAYERS,
});

export const deactivateSpatialLayerStatus = inActiveID => {
  return {
    type: DEACTIVATE_SPATIAL_LAYER,
    inActiveID,
  };
};

/**
 * Fetches all spatial-layers.
 */
export const fetchSpatialLayers = orgId => (dispatch, getState) => {
  dispatch(fetchSpatialLayersStart());

  const fetchRetry = createFetchRetry({ getState });

  return fetchRetry(`${baseURL}/v1/spatial-layers/portal/${orgId}`)
    .then(checkStatus)
    .then(res => parseJSON(res))
    .then(res => {
      const layers = res.layers;
      const aviveAEDRequestLayer = layers.find(
        layer => layer.key === 'avive_aed_request',
      );

      // The avive_aed_request layer is a dynamic layer, but it's not aware
      // of its avive status since it arrives separately as ADR. When fetching
      // the spatial layers, we need to pick out the avive_aed_request layer
      // to flag if any of its spatial objects are currently inactive.
      if (aviveAEDRequestLayer) {
        // For each of the objects get the phone number, then use it to
        // look it up in the state.avive slice to see if it's timed out,
        // concluded, or canceled. If it is, then set the inactiveRequest
        // attribute to true.
        const aviveStatuses = getState().avive?.statuses;
        const aviveRequests = aviveAEDRequestLayer.spatial_objects;
        aviveRequests.forEach(req => {
          const phone = normalizePhone(req?.attributes?.phone_number);
          const aviveStatus = aviveStatuses[`tel://${phone}`];
          const status = aviveStatus?.data?.emergency_status_update;

          const AVIVE_RESOLVED_STATUSES = [
            AVIVE2_STATUS_EMERGENCY_TIMEOUT,
            AVIVE2_STATUS_AED_CONCLUDED,
            AVIVE2_STATUS_AED_CANCELED,
          ];

          if (status && AVIVE_RESOLVED_STATUSES.includes(status)) {
            // Set the layer key in the spatial object so it can be picked out
            // in the getPick of the IconLayer drawing the spatial object in
            // DynamicLayerGL.
            req.key = 'avive_aed_request';
            // Add flag so the avive_aed_request spatial object knows it's inactive.
            req.attributes.inactiveRequest = true;
          }
        });
      }

      const { staticLayers, dynamicLayers } = processLayersResponse(
        layers,
        getState,
      );

      dispatch(
        fetchSpatialLayersSuccess({
          staticLayers,
          dynamicLayers,
        }),
      );
    })
    .catch(async error => {
      const serializedError = await serializeRequestError(error);
      dispatch(fetchSpatialLayersFailed(serializedError));
    });
};

export const getLatestActiveSpatialLayer = message => ({
  type: GET_LATEST_ACTIVE_SPATIAL_LAYERS,
  message,
});

export const setActiveCRGObject =
  (spatialObject, deeplinkData = null) =>
  (dispatch, getState) => {
    // If we are explicitly closing the modal (spatialObject is null), reset state
    if (!spatialObject) {
      dispatch({
        type: SET_ACTIVE_CRG_OBJECT,
        payload: {
          spatialObject: {},
          activeFloor: {},
          deeplink: {
            loading: false,
            error: null,
            data: {},
          },
        },
      });
      return;
    }

    const { activeCrgObject } = getState().spatialLayers;
    const currentId = activeCrgObject?.spatialObject?.id;
    const newId = spatialObject.id;
    const shouldPreserveDeeplink = currentId === newId;

    const floors = spatialObject?.attributes?.floors;

    // Determine deeplink state
    let deeplinkState;
    if (deeplinkData) {
      deeplinkState = {
        loading: false,
        error: null,
        data: deeplinkData,
      };
    } else if (shouldPreserveDeeplink) {
      deeplinkState = activeCrgObject.deeplink;
    } else {
      deeplinkState = {
        loading: false,
        error: null,
        data: {},
      };
    }

    dispatch({
      type: SET_ACTIVE_CRG_OBJECT,
      payload: {
        ...activeCrgObject,
        activeFloor: floors ? findCrgDefaultFloor(floors) : null,
        spatialObject: {
          ...spatialObject,
        },
        deeplink: deeplinkState,
      },
    });
  };

export const setActiveCRGObjectFloor = floor => ({
  type: SET_ACTIVE_CRG_OBJECT_FLOOR,
  floor,
});
export const toggleSpatialLayer = (layerKey, isDynamic, isChecked) => ({
  type: TOGGLE_SPATIAL_LAYER,
  layerKey,
  isDynamic,
  isChecked,
});
export const toggleSpatialLayerGroup = layersKeys => ({
  type: TOGGLE_SPATIAL_LAYER_GROUP,
  layersKeys,
});
export const updateDisabledDynamicLayers = (layerKey, isDisabled) => ({
  type: UPDATE_DISABLED_DYNAMIC_LAYERS,
  layerKey,
  isDisabled,
});

export const updateDynamicLayer = layer => ({
  type: UPDATE_DYNAMIC_LAYER,
  layer,
});

export const fetchCRGDeeplinkStart = () => ({
  type: FETCH_CRG_DEEPLINK_START,
});

export const fetchCRGDeeplinkSuccess = data => ({
  type: FETCH_CRG_DEEPLINK_SUCCESS,
  data,
});

export const fetchCRGDeeplinkFailed = error => ({
  type: FETCH_CRG_DEEPLINK_FAILED,
  error,
});

export const fetchCRGDeeplink =
  (ordID, spatialObjectID) => async (dispatch, getState) => {
    dispatch(fetchCRGDeeplinkStart());
    const token = getTokens()?.token;
    const fetchRetry = createFetchRetry({ getState });
    return fetchRetry(`${baseURL}/v1/deeplink/crg/${ordID}`, {
      method: 'POST',
      body: JSON.stringify({ spatial_object_id: spatialObjectID }),
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      credentials: 'same-origin',
    })
      .then(checkStatus)
      .then(res => parseJSON(res))
      .then(res => {
        dispatch(fetchCRGDeeplinkSuccess(res));
        return res;
      })
      .catch(async error => {
        const serializedError = await serializeRequestError(error);
        dispatch(fetchCRGDeeplinkFailed(serializedError));
      });
  };

export const forceStaticLayerOn = layerSource => ({
  type: FORCE_STATIC_LAYER_ON,
  layerSource,
});

export const inactivateAviveRequest = phone => ({
  type: INACTIVATE_AVIVE_REQUEST,
  phone,
});

export const setStaticSpatialObjectActiveStatus = payload => ({
  type: SET_STATIC_SPATIAL_OBJECT_ACTIVE_STATUS,
  payload,
});

export const setStaticSpatialObjectInactiveStatus = payload => ({
  type: SET_STATIC_SPATIAL_OBJECT_INACTIVE_STATUS,
  payload,
});

/**
 * Fetch spatial objects summary (lightweight, no attributes/floors)
 * Used for initial map load when feature flag is enabled
 */
export const fetchSpatialObjectsSummary =
  orgId => async (dispatch, getState) => {
    const fetchRetry = createFetchRetry({ getState });

    try {
      const response = await fetchRetry(
        `${baseURL}/v1/spatial-layers/portal/${orgId}/objects`,
      )
        .then(checkStatus)
        .then(res => parseJSON(res));

      const { staticLayers, dynamicLayers } = processLayersResponse(
        response.layers,
        getState,
      );

      dispatch({
        type: GET_SPATIAL_OBJECTS_SUMMARY_SUCCESS,
        payload: {
          staticLayers,
          dynamicLayers,
          orgId,
        },
      });
    } catch (error) {
      const serializedError = await serializeRequestError(error);
      dispatch({
        type: GET_SPATIAL_OBJECTS_SUMMARY_FAILURE,
        payload: {
          error: serializedError,
          orgId,
        },
      });
    }
  };

const buildObjectDetailsPayload = (object, objectId, getState) => {
  const { spatialLayers } = getState();
  const { objectDetailsCache, activeCrgObject } = spatialLayers;

  // Update cache
  const newCache = {
    ...objectDetailsCache,
    [objectId]: object,
  };

  // If the currently active object matches the fetched object, update it
  let newActiveCrgObject = activeCrgObject;
  if (activeCrgObject?.spatialObject?.id === objectId) {
    const floors = object.attributes?.floors;
    newActiveCrgObject = {
      ...activeCrgObject,
      activeFloor: floors ? findCrgDefaultFloor(floors) : null,
      spatialObject: object,
      deeplink: {
        ...activeCrgObject.deeplink,
        loading: false,
      },
    };
  }

  return {
    objectDetailsCache: newCache,
    activeCrgObject: newActiveCrgObject,
  };
};

/**
 * Fetch complete spatial object details (includes attributes.floors)
 * Called when user clicks on a CRG building marker
 */
export const fetchSpatialObjectDetails =
  (orgId, objectId) => async (dispatch, getState) => {
    const { spatialLayers } = getState();

    // Check if already cached
    if (spatialLayers.objectDetailsCache?.[objectId]) {
      const cachedObject = spatialLayers.objectDetailsCache[objectId];
      dispatch({
        type: GET_SPATIAL_OBJECT_DETAILS_SUCCESS,
        payload: buildObjectDetailsPayload(cachedObject, objectId, getState),
      });
      return Promise.resolve(cachedObject);
    }

    dispatch({ type: FETCH_SPATIAL_OBJECT_DETAILS_START });
    const fetchRetry = createFetchRetry({ getState });

    try {
      const response = await fetchRetry(
        `${baseURL}/v1/spatial-layers/portal/${orgId}/objects/${objectId}/details`,
      )
        .then(checkStatus)
        .then(res => parseJSON(res));

      dispatch({
        type: GET_SPATIAL_OBJECT_DETAILS_SUCCESS,
        payload: buildObjectDetailsPayload(response, objectId, getState),
      });

      return response;
    } catch (error) {
      const serializedError = await serializeRequestError(error);
      dispatch({
        type: FETCH_SPATIAL_OBJECT_DETAILS_FAILED,
        error: serializedError,
      });
      throw serializedError;
    }
  };

/**
 * Resolves the full spatial object details.
 * If optimized endpoints are enabled, it checks the cache (via fetchSpatialObjectDetails)
 * or fetches the details.
 */
export const resolveSpatialObject =
  (spatialObject, hasOptimizedEndpoints) => async (dispatch, getState) => {
    if (hasOptimizedEndpoints) {
      const { currentOrg } = getState().sinatra.user;
      if (!currentOrg) {
        return undefined;
      }
      try {
        // fetchSpatialObjectDetails checks the cache internally
        return await dispatch(
          fetchSpatialObjectDetails(currentOrg.id, spatialObject.id),
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to resolve spatial object details', error);
        return undefined;
      }
    }
    return undefined;
  };
